import { useSearchParams } from "react-router-dom";

import { actions, Datasource } from "@/shared/state/visualization";
import {
  useDatasources,
  useVizDispatch,
} from "@/shared/state/visualization/hooks";

import { useWorkspaceLoaderData } from "../loader";

export function useAddFilesToWorkspace() {
  const dispatch = useVizDispatch();
  const [, setSearchParams] = useSearchParams();
  const workspaceLoaderData = useWorkspaceLoaderData();
  const datasources = useDatasources();

  // Check if we've loaded a shared workspace
  const isSharedWorkspace = workspaceLoaderData?.type === "workspace";

  const addFiles = (newFileIds: string[]) => {
    // Get existing dataset source (if any)
    const datasetSource = datasources.find((ds) => ds.type === "dataset");

    // Get existing file IDs from datasources
    const existingFileIds = datasources
      .filter((ds) => ds.type === "file")
      .map((ds) => ds.value.fileId);

    // Merge and dedupe file IDs
    const allFileIds = [...new Set([...existingFileIds, ...newFileIds])];

    // Build new datasources array
    const newDatasources: Datasource[] = [
      // Keep dataset source if present
      ...(datasetSource ? [datasetSource] : []),
      // Add all file sources
      ...allFileIds.map((fileId) => ({
        type: "file" as const,
        value: { fileId },
      })),
    ];

    // Update viz state with new datasources
    dispatch(actions.setDatasources(newDatasources));

    // If shared workspace, don't update URL
    if (isSharedWorkspace) {
      return;
    }

    // Sync to URL (non-workspace mode)
    setSearchParams(
      (oldParams) => {
        const params = new URLSearchParams(oldParams);

        // Keep dataset param if present in datasources
        if (datasetSource) {
          params.set("dataset", datasetSource.value.datasetId);
        }

        // Update file params
        params.delete("file");
        allFileIds.forEach((fileId) => {
          params.append("file", fileId);
        });

        return params;
      },
      { preventScrollReset: true, replace: true },
    );
  };

  return { addFiles };
}
